import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, FileDown, Loader2, Mail, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile } from "@/lib/pdf";
import { useT } from "@/i18n";
import { isAddress, isFullName, isNip, isPesel, isRegon } from "@/lib/validate";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";
import SignActions from "@/components/landing/SignActions";
import CompanyLookup from "@/components/landing/CompanyLookup";
import { useFormGuard } from "@/lib/formGuard";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { OptionField } from "@/components/OptionField";
import { ListField } from "@/components/ListField";
import { StaffShell } from "@/pages/StaffDocuments";

const isDateField = (f) => /^(data|date|datum|дата|tarih)\b/i.test(f.label.trim());

// Pola dat odnoszące się do zdarzeń przeszłych (data wcześniej zawartej umowy / wcześniejszej zmiany danych spółki)
const PAST_DATE_OK = /(data zawarcia umowy|data zmiany)/i;
const allowPastDate = (f) => PAST_DATE_OK.test((f.key || "").replace(/[{}]/g, "")) || PAST_DATE_OK.test(f.label || "");

export default function DocumentGenerator({ staff = false }) {
  if (staff) return <StaffShell backTo={ROUTES.staff} backLabel="Dokumenty wewnętrzne"><div className="max-w-2xl mx-auto"><GeneratorCard staff /></div></StaffShell>;
  return <PublicPage />;
}

function PublicPage() {
  const { t } = useT();
  const { docId } = useParams();
  const [doc, setDoc] = useState(null);
  useSeo({ title: `${doc?.name || "Dokument"} — generator PDF`, description: doc?.name ? `${doc.name} dla klientów wirtualnego biura w Warszawie — wypełnij online i pobierz gotowy PDF do podpisu profilem zaufanym.` : undefined, path: ROUTES.doc(docId), noindex: !!doc?.prefill, breadcrumbs: [{ name: "Dokumenty", path: ROUTES.docs }, { name: doc?.name || "Dokument", path: ROUTES.doc(docId) }] });
  return (
    <div data-testid="document-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to={doc?.public ? "/biuro-wirtualne-warszawa/dokumenty" : "/"} data-testid="document-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {doc?.public ? t.docs.back_list : t.footer.back_home}
          </Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
        <GeneratorCard onDoc={setDoc} />
      </main>
    </div>
  );
}

function GeneratorCard({ staff = false, onDoc }) {
  const { t } = useT();
  const { docId } = useParams();
  const [params] = useSearchParams();
  const order = staff ? null : params.get("order");
  const token = staff ? null : params.get("t");
  const base = staff ? `/staff/documents/${docId}` : `/documents/${docId}`;
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const fg = useFormGuard();
  const guard = staff ? async () => ({}) : fg.guard;
  const Honeypot = staff ? () => null : fg.Honeypot;
  const GuardNotice = staff ? () => null : fg.GuardNotice;
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [regManual, setRegManual] = useState(false);
  const onLookupStatus = (s) => { if (s === "unavailable") setRegManual(true); else if (s === "found") setRegManual(false); };

  const isHidden = (f) => !!f.depends && !(values[f.depends.key] || "").trim().startsWith(f.depends.option);

  const validate = () => {
    const er = {};
    doc.fields.forEach((f) => {
      if (isHidden(f)) return;
      const v = (values[f.key] || "").trim();
      if (!f.optional && !v) { er[f.key] = t.errors.required; return; }
      if (!v) return;
      if (f.key === "{NIP}") { if (!isNip(v)) er[f.key] = t.errors.nip; return; }
      if (f.key === "{REGON}") { if (!isRegon(v)) er[f.key] = t.errors.regon; return; }
      if (f.key === "{PESEL}") { if (!isPesel(v)) er[f.key] = t.errors.pesel; return; }
      if (f.repeat || (f.options && f.options.length) || isDateField(f)) return;
      const label = (f.label || "").toLowerCase();
      if (/adres|address|adresse|адрес/.test(label)) {
        if (!isAddress(v)) er[f.key] = t.errors.address;
      } else if (f.key !== "{nazwa firmy}" && /(imię|imie|nazwisko|osoba|reprezent|pełnomoc|pelnomoc|upoważ|upowaz|podpis)/.test(label)) {
        if (!isFullName(v)) er[f.key] = t.errors.name;
      }
    });
    setErrors(er);
    if (Object.keys(er).length) toast.error(t.errors.summary);
    return !Object.keys(er).length;
  };

  const sendEmail = async () => {
    if (!validate()) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error(t.errors.email);
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/documents/${docId}/email`, { fields: values, email: email.trim(), ...(await guard()) }, { params: order && token ? { order, t: token } : {} });
      setPending({ email: r.data.to, id: r.data.id });
      setResent(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : t.order.pdf_err);
    } finally {
      setSending(false);
    }
  };

  const resendDoc = async () => {
    if (!pending?.id) return;
    setResending(true);
    try {
      await api.post(`/documents/email/${pending.id}/resend`);
      setResent(true);
      toast.success(t.verify.resent);
    } catch (err) {
      toast.error(err.response?.status === 429 ? t.errors.too_many : t.verify.resend_err);
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const params = order && token ? { order, t: token } : {};
    api.get(base, { params }).then((r) => {
      setDoc(r.data); onDoc?.(r.data);
      const init = r.data.prefill ? { ...r.data.prefill } : {};
      const d = new Date();
      const today = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
      (r.data.fields || []).forEach((f) => { if (isDateField(f) && !init[f.key]) init[f.key] = today; });
      setValues(init);
    }).catch(() => setError(true));
  }, [docId, order, token, t.docs.title]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await downloadFile({ method: "post", url: `${base}/pdf`, params: order && token ? { order, t: token } : {}, data: { fields: values, ...(await guard()) } }, `${doc.name}.pdf`);
      toast.success(t.docs.done);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : t.order.pdf_err);
    } finally {
      setBusy(false);
    }
  };

  return (
        <div className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10">
          {error ? (
            <div className="text-center">
              <XCircle size={44} className="mx-auto text-brand-red" />
              <h1 data-testid="document-title" className="font-display font-bold text-2xl mt-5">{t.payment.error_title}</h1>
              <p className="text-sm text-muted-foreground mt-3">{t.payment.error_sub}</p>
            </div>
          ) : !doc ? (
            <Loader2 data-testid="document-loading" className="animate-spin mx-auto text-slate-500" size={32} />
          ) : (
            <form onSubmit={generate} noValidate data-testid="document-form">
              <Honeypot />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">
                <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-yellow mr-3 align-middle" />{t.docs.title}
              </p>
              <h1 data-testid="document-title" className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-3">{doc.name}</h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                {staff ? (doc.description || "Dokument wewnętrzny — wypełnij pola i pobierz PDF.") : doc.public ? t.docs.intro_public : <>{t.docs.intro} <strong className="text-foreground">{doc.company}</strong>. {t.docs.intro2}</>}
              </p>
              <div className="mt-8 space-y-5">
                {(() => { const lockReg = !!doc && doc.fields.some((x) => x.key === "{NIP}" || x.key === "{REGON}") && !regManual; return [...doc.fields].sort((a, b) => (a.key === "{NIP}" ? -1 : b.key === "{NIP}" ? 1 : 0)).map((f, i) => isHidden(f) ? null : (
                  <div key={f.key}>
                    <label className="text-xs font-semibold text-muted-foreground mb-1.5 block first-letter:uppercase">{f.label}{f.optional ? "" : " *"}</label>
                    {f.repeat ? (
                      <ListField
                        subfields={f.subfields}
                        testId={`document-field-${i}`}
                        invalid={!!errors[f.key]}
                        onChange={(v) => {
                          setValues((prev) => ({ ...prev, [f.key]: v }));
                          if (errors[f.key]) setErrors({ ...errors, [f.key]: undefined });
                        }}
                      />
                    ) : f.options?.length ? (
                      <OptionField
                        field={f}
                        testId={`document-field-${i}`}
                        value={values[f.key] || ""}
                        invalid={!!errors[f.key]}
                        pickDate={t.docs.pick_date}
                        extraPlaceholder={t.docs.extra_placeholder}
                        onChange={(v) => {
                          setValues({ ...values, [f.key]: v });
                          if (errors[f.key]) setErrors({ ...errors, [f.key]: undefined });
                        }}
                      />
                    ) : isDateField(f) ? (
                      <DateField
                        testId={`document-field-${i}`}
                        value={values[f.key] || ""}
                        invalid={!!errors[f.key]}
                        allowPast={allowPastDate(f)}
                        placeholder={t.docs.pick_date}
                        onChange={(v) => {
                          setValues({ ...values, [f.key]: v });
                          if (errors[f.key]) setErrors({ ...errors, [f.key]: undefined });
                        }}
                      />
                    ) : (
                    <Input
                      data-testid={`document-field-${i}`}
                      value={values[f.key] || ""}
                      aria-invalid={!!errors[f.key]}
                      readOnly={lockReg && (f.key === "{nazwa firmy}" || f.key === "{adres}")}
                      onChange={(e) => {
                        if (lockReg && (f.key === "{nazwa firmy}" || f.key === "{adres}")) return;
                        setValues({ ...values, [f.key]: e.target.value });
                        if (errors[f.key]) setErrors({ ...errors, [f.key]: undefined });
                      }}
                      placeholder={lockReg && (f.key === "{nazwa firmy}" || f.key === "{adres}") ? (t.order.wizard?.company_locked_ph || "") : ""}
                      className={`bg-background ${lockReg && (f.key === "{nazwa firmy}" || f.key === "{adres}") ? "opacity-80 cursor-not-allowed" : ""} ${errors[f.key] ? "border-brand-red focus-visible:ring-brand-red" : ""}`}
                    />
                    )}
                    {errors[f.key] && <p className="text-xs text-brand-red mt-1">{errors[f.key]}</p>}
                    {lockReg && f.key === "{nazwa firmy}" && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{t.order.wizard?.company_locked_hint || ""}</p>}
                    {regManual && f.key === "{nazwa firmy}" && <p className="text-[11px] text-brand-red mt-1.5 leading-relaxed">{t.order.wizard?.registry_unavailable || ""}</p>}
                    {(f.key === "{NIP}" || f.key === "{REGON}") && (
                      <CompanyLookup nip={values[f.key]} by={f.key === "{REGON}" ? "regon" : "nip"} testId={f.key === "{REGON}" ? "document-company-lookup-regon" : "document-company-lookup"} auto onStatus={onLookupStatus} onFill={(d) => {
                        const keys = doc.fields.map((x) => x.key);
                        const fill = { "{nazwa firmy}": d.name, "{adres}": d.address, "{KRS}": d.krs, "{REGON}": d.regon, "{NIP}": d.nip, "{forma prawna}": t.order.legal_forms?.[d.legal_form] || d.legal_form_name };
                        setValues((prev) => {
                          const next = { ...prev };
                          Object.entries(fill).forEach(([k, v]) => { if (keys.includes(k) && v && k !== f.key) next[k] = v; });
                          return next;
                        });
                        setErrors((prev) => {
                          const next = { ...prev };
                          Object.keys(fill).forEach((k) => { next[k] = undefined; });
                          return next;
                        });
                      }} />
                    )}
                    {f.key === "{NIP}" && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{t.order.wizard?.nip_lookup_hint || ""}</p>}
                    {f.key === "{REGON}" && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{t.lookup.regon_lookup_hint || ""}</p>}
                  </div>
                )); })()}
              </div>
              <GuardNotice />
              {(staff || order) && (
                <button
                  type="submit"
                  data-testid="document-generate-button"
                  disabled={busy}
                  className="mt-8 w-full sm:w-auto bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {t.docs.generate}
                </button>
              )}
              {!staff && (<>
              <div data-testid="document-email-box" className="mt-8 rounded-2xl bg-slate-100 dark:bg-slate-800 p-4 sm:p-5">
                <p className="text-xs font-semibold text-muted-foreground">{t.docs.email_title}</p>
                {pending ? (
                  <div data-testid="document-email-pending" className="mt-2">
                    <p className="text-sm leading-relaxed inline-flex items-start gap-2"><Mail size={16} className="text-brand-teal shrink-0 mt-0.5" /> <span>{t.verify.doc_sent.replace("{email}", pending.email)}</span></p>
                    <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{t.verify.doc_note}</p>
                    <button type="button" data-testid="document-email-resend" onClick={resendDoc} disabled={resending || resent}
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark disabled:opacity-60 transition-colors">
                      {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {resent ? t.verify.resent : t.verify.resend}
                    </button>
                  </div>
                ) : (<>
                  <div className="mt-2 flex flex-col sm:flex-row gap-2">
                    <Input data-testid="document-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.docs.email_ph} className="bg-background flex-1" />
                    <button
                      type="button"
                      data-testid="document-email-button"
                      onClick={sendEmail}
                      disabled={sending}
                      className={order
                        ? "inline-flex items-center justify-center gap-2 whitespace-nowrap border-2 border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white disabled:opacity-60 font-semibold px-5 py-2 rounded-full text-sm transition-colors"
                        : "inline-flex items-center justify-center gap-2 whitespace-nowrap bg-brand-teal hover:bg-brand-tealDark text-white disabled:opacity-60 font-semibold px-5 py-2 rounded-full text-sm transition-colors"}
                    >
                      {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} {t.docs.send_email}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{t.verify.doc_note}</p>
                </>)}
              </div>
              <Link to="/biuro-wirtualne-warszawa/dokumenty#biuro-wirtualne-poradnik" data-testid="document-guide-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
                <BookOpen size={15} /> {t.docs.guide_cta}
              </Link>
              <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">{t.docs.note}</p>
              <div data-testid="document-upload-section" className="mt-8 pt-6 border-t border-border">
                <p className="text-sm font-semibold mb-3">{t.upload.after_generate}</p>
                <SignActions compact />
              </div>
              </>)}
            </form>
          )}
        </div>
  );
}
